import React, { useState } from "react";
import { Group, SegmentedControl, Switch } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { SettingsField, SettingsTabShell } from "../common";
import {
  CommissionAdvancedSettingsValues,
  buildCommissionAdvancedSettingsPayload,
  normalizeCommissionAdvancedSettings,
} from "../../../utils/commissionUtils";
import { BusinessChargeCommissionMode } from "../../../types/salon";

export interface CommissionsAdvancedSettingsTabProps {
  businessInfo: any;
  editing: boolean;
  saving?: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (payload: Record<string, any>) => void | Promise<void>;
}

const yesNo = (v?: boolean) => (v ? "Yes" : "No");

/** Friendly read-mode label for the business-charge mode; absent/falsy → the
 *  default "Sale Value". */
const businessChargeModeLabel = (v?: string) =>
  v === "commission" ? "Commission" : "Sale Value";

/**
 * Commissions › Advanced Settings. Houses salon-level toggles that govern how
 * commissions are calculated. Uses the shared inline read/edit pattern
 * (SettingsTabShell + SettingsField) so it matches the rest of the redesigned
 * Settings surfaces. Persists via PUT /business-locations through the page-level save
 * handler, exactly like My Business.
 */
export default function CommissionsAdvancedSettingsTab({
  businessInfo,
  editing,
  saving = false,
  readOnly = false,
  onEdit,
  onCancel,
  onSave,
}: CommissionsAdvancedSettingsTabProps) {
  const [values, setValues] = useState<CommissionAdvancedSettingsValues>(() =>
    normalizeCommissionAdvancedSettings(businessInfo),
  );
  const isLargeScreen = useMediaQuery("(min-width: 769px)");

  // On larger screens the edit column is wide; center the control within it (in
  // edit mode only) so it isn't marooned at the far right. Read mode and mobile
  // keep the default alignment.
  const centerControl = (node: React.ReactNode) =>
    isLargeScreen ? (
      <Group justify="center" w="100%">
        {node}
      </Group>
    ) : (
      node
    );

  const startEdit = () => {
    setValues(normalizeCommissionAdvancedSettings(businessInfo));
    onEdit();
  };

  const setField = (
    field: keyof CommissionAdvancedSettingsValues,
    value: any,
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Include the salon id — PUT /business-locations keys the update off it — and only this
    // view's fields.
    onSave({
      id: businessInfo?.id,
      ...buildCommissionAdvancedSettingsPayload(values),
    });
  };

  return (
    <SettingsTabShell
      editing={editing}
      readOnly={readOnly}
      saving={saving}
      onEdit={startEdit}
      onCancel={onCancel}
      onSave={handleSave}
    >
      <SettingsField
        label="Deduct Discounts from Employee Commissions"
        info="When enabled, employee commissions are calculated after discounts are applied rather than on the full price."
        editing={editing}
        withDivider
        editControl={centerControl(
          <Switch
            aria-label="Deduct Discounts from Employee Commissions"
            checked={values.enable_discount_deduction_on_commissions}
            onChange={(e) =>
              setField(
                "enable_discount_deduction_on_commissions",
                e.currentTarget.checked,
              )
            }
          />,
        )}
      >
        {yesNo(businessInfo?.enable_discount_deduction_on_commissions)}
      </SettingsField>

      <SettingsField
        label="Business Charge Deduction"
        info="Choose how a per-item business charge reduces staff commission. 'Sale Value' subtracts the charge from the sale value before the rate is applied (default); 'Commission' subtracts it from the commission after the rate. Changing this recalculates commissions for existing unpaid periods."
        editing={editing}
        withDivider={false}
        editControl={centerControl(
          <SegmentedControl
            aria-label="Business Charge Deduction"
            data={[
              { label: "Sale Value", value: "sale_value" },
              { label: "Commission", value: "commission" },
            ]}
            value={values.business_charge_commission_mode}
            onChange={(v) =>
              setField(
                "business_charge_commission_mode",
                v as BusinessChargeCommissionMode,
              )
            }
          />,
        )}
      >
        {businessChargeModeLabel(businessInfo?.business_charge_commission_mode)}
      </SettingsField>
    </SettingsTabShell>
  );
}
